import React, { useState } from 'react';
import axios from 'axios';
import { Calendar, Clock, User, Mail, Phone, X, MessageSquare, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AppointmentBooking = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    service: '',
    date: '',
    time: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });

  const services = [
    'Aides à la personne',
    'Garde d\'enfant',
    'Ménage et repassage',
    'Bricolage et jardinage',
    'Informatique pour particulier'
  ];

  const timeSlots = [
    '09:00', '10:00', '11:00', '14:00', '15:00', '16:00', '17:00'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.post(`${API_URL}/api/appointment`, formData);
      
      if (response.data.status === 'success') {
        setIsSubmitted(true);
        
        // Reset after 3 seconds
        setTimeout(() => {
          setIsOpen(false);
          setIsSubmitted(false);
          setCurrentStep(1);
          setFormData({
            service: '',
            date: '',
            time: '',
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            message: ''
          });
        }, 3000);
      }
    } catch (error) {
      console.error('Appointment booking error:', error);
      alert(error.response?.data?.detail || 'Une erreur est survenue. Veuillez réessayer.');
    }
  };

  const canProceedToStep2 = formData.service && formData.date && formData.time;
  const canSubmit = canProceedToStep2 && formData.firstName && formData.lastName && formData.email && formData.phone;

  return (
    <>
      {/* Floating Button */}
      <button 
        className="appointment-floating-btn"
        onClick={() => setIsOpen(true)}
        aria-label="Prendre rendez-vous"
      >
        <Calendar size={24} />
        <span>Prendre RDV</span>
      </button>

      {/* Modal */}
      {isOpen && (
        <>
          <div className="appointment-overlay" onClick={() => setIsOpen(false)}></div>
          
          <div className="appointment-modal">
            <button 
              className="appointment-close"
              onClick={() => setIsOpen(false)}
              aria-label="Fermer"
            >
              <X size={24} />
            </button>

            {!isSubmitted ? (
              <>
                <div className="appointment-header">
                  <Calendar size={32} className="appointment-icon" />
                  <h2 className="heading-1">Prendre rendez-vous</h2>
                  <p className="body-medium">Réservez votre créneau en quelques clics</p>
                </div>

                <form onSubmit={handleSubmit} className="appointment-form">
                  {/* Step 1: Service, Date & Time */}
                  {currentStep === 1 && (
                    <div className="appointment-step">
                      <h3 className="heading-2">Choisissez votre service et créneau</h3>
                      
                      <div className="form-group">
                        <label>Service souhaité *</label>
                        <select
                          name="service"
                          value={formData.service}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Sélectionnez un service</option>
                          {services.map((service, index) => (
                            <option key={index} value={service}>{service}</option>
                          ))}
                        </select>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>Date souhaitée *</label>
                          <input
                            type="date"
                            name="date"
                            value={formData.date}
                            onChange={handleInputChange}
                            min={new Date().toISOString().split('T')[0]}
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label>Heure *</label>
                          <select
                            name="time"
                            value={formData.time}
                            onChange={handleInputChange}
                            required
                          >
                            <option value="">Sélectionnez</option>
                            {timeSlots.map((time, index) => (
                              <option key={index} value={time}>{time}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="btn-primary"
                        onClick={() => setCurrentStep(2)}
                        disabled={!canProceedToStep2}
                      >
                        Continuer
                      </button>
                    </div>
                  )}

                  {/* Step 2: Contact Info */}
                  {currentStep === 2 && (
                    <div className="appointment-step">
                      <h3 className="heading-2">Vos coordonnées</h3>
                      
                      <div className="form-row">
                        <div className="form-group">
                          <label>Prénom *</label>
                          <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label>Nom *</label>
                          <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Email *</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Téléphone *</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Message (optionnel)</label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          rows="3"
                          placeholder="Informations complémentaires..."
                        ></textarea>
                      </div>

                      <div className="appointment-actions">
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => setCurrentStep(1)}
                        >
                          Retour
                        </button>
                        <button
                          type="submit"
                          className="btn-primary"
                          disabled={!canSubmit}
                        >
                          Confirmer le rendez-vous
                        </button>
                      </div>
                    </div>
                  )}
                </form>

                {/* Summary on step 2 */}
                {currentStep === 2 && (
                  <div className="appointment-summary">
                    <h4>Récapitulatif</h4>
                    <div className="summary-item">
                      <MessageSquare size={18} />
                      <span>{formData.service}</span>
                    </div>
                    <div className="summary-item">
                      <Calendar size={18} />
                      <span>{new Date(formData.date).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    </div>
                    <div className="summary-item">
                      <Clock size={18} />
                      <span>{formData.time}</span>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="appointment-success">
                <div className="success-icon">
                  <CheckCircle2 size={64} />
                </div>
                <h2 className="heading-1">Rendez-vous confirmé !</h2>
                <p className="body-medium">
                  Nous avons bien reçu votre demande de rendez-vous.<br />
                  Un email de confirmation vous a été envoyé.
                </p>
                <p className="body-medium success-details">
                  <strong>{formData.service}</strong><br />
                  Le {new Date(formData.date).toLocaleDateString('fr-FR')} à {formData.time}
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};
